import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { TRAY_INFO, CONTACT, CATEGORY_OPTIONS } from "@/lib/data";
import { fetchMenuItems, resolveImg } from "@/lib/api";

const HEADER_IMG =
  "https://images.unsplash.com/photo-1716550781939-beb7d7247aae?crop=entropy&cs=srgb&fm=jpg&q=90&w=2000";

const FALLBACK_IMG =
  "https://images.unsplash.com/photo-1585937421612-70a008356fbe?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

function groupByCategory(items) {
  const map = {};
  items.forEach((it) => {
    if (!map[it.category]) map[it.category] = [];
    map[it.category].push(it);
  });
  const known = CATEGORY_OPTIONS.filter((c) => map[c]);
  const extra = Object.keys(map).filter((c) => !CATEGORY_OPTIONS.includes(c));
  return [...known, ...extra].map((cat) => ({ category: cat, items: map[cat] }));
}

function PriceChips({ item }) {
  const chips = [];
  if (item.half_tray_price) chips.push({ label: "Half Tray", value: `$${item.half_tray_price}`, color: "#C92A6B" });
  if (item.full_tray_price) chips.push({ label: "Full Tray", value: `$${item.full_tray_price}`, color: "#D4AF37" });
  if (item.serves_price) chips.push({ label: item.serves_qty ? `Serves ${item.serves_qty}` : "Serves", value: `$${item.serves_price}`, color: "#0F7A54" });
  if (chips.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-4">
      {chips.map((c) => (
        <span key={c.label} className="inline-flex items-center gap-2 border border-[#D4AF37]/25 rounded-full px-3 py-1.5">
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: c.color }} />
          <span className="font-body text-[11px] tracking-wide uppercase text-[#A89F91]">{c.label}</span>
          <span className="font-display text-base leading-none text-[#FDFBF7]">{c.value}</span>
        </span>
      ))}
    </div>
  );
}

export default function MenuPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchMenuItems()
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const groups = groupByCategory(items);

  return (
    <div className="bg-pof min-h-screen">
      <section className="relative pt-32 md:pt-44 pb-16 md:pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HEADER_IMG} alt="Catering feast" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0607] via-[#0A0607]/70 to-[#0A0607]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <Link to="/" data-testid="menu-back-home" className="inline-flex items-center gap-2 font-body text-xs tracking-[0.2em] uppercase text-[#A89F91] hover:text-[#D4AF37] transition-colors mb-10">
            <ArrowLeft size={15} /> Back Home
          </Link>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="font-body text-xs tracking-[0.4em] uppercase text-[#D4AF37] mb-5">
            Catering Menu · {CONTACT.area}
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }} className="font-display text-5xl sm:text-7xl lg:text-8xl leading-[0.92] text-[#FDFBF7]">
            The <span className="gold-gradient-text italic">Royal</span> Trays
          </motion.h1>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }} className="mt-8 flex flex-col sm:flex-row gap-4 sm:gap-8">
            <span className="inline-flex items-center gap-3 font-body text-sm text-[#A89F91]"><span className="w-2 h-2 rounded-full bg-[#C92A6B]" /> {TRAY_INFO.half}</span>
            <span className="inline-flex items-center gap-3 font-body text-sm text-[#A89F91]"><span className="w-2 h-2 rounded-full bg-[#D4AF37]" /> {TRAY_INFO.full}</span>
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-36">
        {loading ? (
          <div className="flex justify-center py-20 text-[#D4AF37]"><Loader2 className="animate-spin" size={32} /></div>
        ) : groups.length === 0 ? (
          <p className="text-center font-body text-[#A89F91] py-20">Menu coming soon. Please check back or contact us for a custom quote.</p>
        ) : (
          <div className="space-y-20 md:space-y-28">
            {groups.map((group) => (
              <div key={group.category}>
                <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }} className="font-display text-3xl md:text-5xl text-[#FDFBF7] mb-10 flex items-center gap-4">
                  <span className="text-[#D4AF37] text-2xl">◆</span> {group.category}
                </motion.h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                  {group.items.map((item, i) => (
                    <motion.div
                      key={item.id}
                      data-testid={`menu-card-${item.id}`}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-40px" }}
                      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: (i % 3) * 0.08 }}
                      className="group flex flex-col rounded-tl-[2rem] rounded-br-[2rem] overflow-hidden border border-[#D4AF37]/15 bg-[#140A0D] hover:border-[#D4AF37]/50 transition-colors duration-500"
                    >
                      <div className="h-52 overflow-hidden">
                        <img src={resolveImg(item.image_url) || FALLBACK_IMG} alt={item.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      </div>
                      <div className="p-6 flex flex-col flex-1">
                        <h3 className="font-display text-2xl text-[#FDFBF7] group-hover:text-[#D4AF37] transition-colors">{item.name}</h3>
                        {item.description ? <p className="font-body text-sm text-[#A89F91] mt-2 flex-1">{item.description}</p> : <div className="flex-1" />}
                        <PriceChips item={item} />
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }} className="mt-28 md:mt-36 text-center border-t border-[#D4AF37]/20 pt-16">
          <p className="font-body text-xs tracking-[0.3em] uppercase text-[#A89F91] mb-4">Custom menus available for every occasion</p>
          <h3 className="font-display text-4xl md:text-6xl text-[#FDFBF7] mb-8">Ready to plan your <span className="gold-gradient-text italic">feast?</span></h3>
          <button data-testid="menu-request-quote" onClick={() => navigate("/#contact")} className="group inline-flex items-center gap-3 px-10 py-4 bg-[#D4AF37] text-[#0A0607] font-body text-sm tracking-[0.2em] uppercase">
            Request a Quote <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </button>
          <p className="font-body text-sm text-[#A89F91] mt-6">or call us at <a href={CONTACT.phoneHref} className="text-[#D4AF37]">{CONTACT.phone}</a></p>
        </motion.div>
      </section>
    </div>
  );
}
